/**
 * XML Parsing utilities for Fianza and Factura documents
 * Extracted from UploadFianza.jsx for reuse in FianzaView.jsx
 */

/**
 * Parse XML Fianza file
 * @param {File} file - The XML file to parse
 * @returns {Promise<{success: boolean, data: object|null, error: string|null, missingFields: string[]}>}
 */
export async function parseXmlFianza(file) {
  try {
    const text = await file.text();
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(text, 'text/xml');

    // Check for parsing errors
    const parseError = xmlDoc.querySelector('parsererror');
    if (parseError) {
      return {
        success: false,
        data: null,
        error: 'Error al analizar el archivo XML',
        missingFields: [],
      };
    }

    const root = xmlDoc.documentElement;

    // Helper function to get attribute value
    const getAttr = (element, attrName) => element?.getAttribute(attrName) || null;

    // Helper function to parse date to timestamp
    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      // Afianzadora XMLs often use dd/mm/yyyy, which new Date() misreads or rejects
      const dmy = dateStr.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (dmy) {
        const [, day, month, year] = dmy;
        const timestamp = new Date(Number(year), Number(month) - 1, Number(day)).getTime();
        return Number.isFinite(timestamp) ? timestamp : null;
      }
      // Bare ISO dates (yyyy-mm-dd) parse as UTC midnight, shifting a day back
      // in local time — anchor them to local midnight instead
      const iso = dateStr.trim().match(/^\d{4}-\d{2}-\d{2}$/);
      const timestamp = new Date(iso ? `${dateStr.trim()}T00:00:00` : dateStr).getTime();
      return Number.isFinite(timestamp) ? timestamp : null;
    };

    // Helper function to parse number
    const parseNumber = (value) => {
      if (!value) return null;
      const parsed = parseFloat(value);
      return isNaN(parsed) ? null : parsed;
    };

    // Extract main attributes from root element
    const numeroFianza = getAttr(root, 'numeroFianza');
    const idPoliza = getAttr(root, 'IdPoliza');
    const numeroCompaniaAfianzadora = getAttr(root, 'numeroCompaniaAfianzadora');
    const folio = getAttr(root, 'folio');
    const montoAfianzado = parseNumber(getAttr(root, 'montoAfianzado'));
    const inicioVigenciaFianza = parseDate(getAttr(root, 'inicioVigenciaFianza'));
    const finVigenciaFianza = parseDate(getAttr(root, 'finVigenciaFianza'));
    const ramo = getAttr(root, 'ramo');
    const subRamo = getAttr(root, 'subRamo');
    const tipo = getAttr(root, 'tipo');
    const fechaSolicitada = parseDate(getAttr(root, 'fechaSolicitada'));
    const fechaAutorizacion = parseDate(getAttr(root, 'fechaAutorizacion'));

    // Extract Emisor data
    const emisor = root.querySelector('Emisor');
    const emisorNombre = emisor ? getAttr(emisor, 'nombre') : null;
    const emisorRfc = emisor ? getAttr(emisor, 'rfc') : null;

    // Extract Fiado data
    const fiado = root.querySelector('Fiado');
    const fiadoNombre = fiado ? getAttr(fiado, 'nombre') : null;
    const fiadoRfc = fiado ? getAttr(fiado, 'rfc') : null;

    // Extract Beneficiario data
    const beneficiario = root.querySelector('Beneficiario');
    const beneficiarioNombre = beneficiario ? getAttr(beneficiario, 'nombre') : null;

    // Extract Agente data
    const agente = root.querySelector('Agente');
    const agenteRfc = agente ? getAttr(agente, 'rfc') : null;
    const agenteNombre = agente ? getAttr(agente, 'nombreAgenteCorredor') : null;

    // Extract Primas data
    const primas = root.querySelector('Primas');
    const prima = primas ? parseNumber(getAttr(primas, 'prima')) : null;
    const bonificacion = primas ? parseNumber(getAttr(primas, 'bonificacion')) : null;
    const derechos = primas ? parseNumber(getAttr(primas, 'derechos')) : null;
    const gastosExpedicion = primas ? parseNumber(getAttr(primas, 'gastosExpedicion')) : null;
    const gastosInvestigacion = primas ? parseNumber(getAttr(primas, 'gastosInvestigacion')) : null;
    const subTotal = primas ? parseNumber(getAttr(primas, 'subTotal')) : null;
    const primaTotal = primas ? parseNumber(getAttr(primas, 'primaTotal')) : null;
    const iva = primas ? parseNumber(getAttr(primas, 'iva')) : null;

    // Calculate tarifa (prima / monto afianzado)
    const tarifa = (prima && montoAfianzado && montoAfianzado !== 0)
      ? (prima / montoAfianzado) * 100
      : null;

    const data = {
      // Main attributes
      numeroFianza,
      idPoliza,
      numeroCompaniaAfianzadora,
      folio,
      montoAfianzado,
      inicioVigenciaFianza,
      finVigenciaFianza,
      ramo,
      subRamo,
      tipo,
      fechaSolicitada,
      fechaAutorizacion,

      // Emisor
      emisor: {
        nombre: emisorNombre,
        rfc: emisorRfc,
      },

      // Fiado
      fiado: {
        nombre: fiadoNombre,
        rfc: fiadoRfc,
      },

      // Beneficiario
      beneficiario: {
        nombre: beneficiarioNombre,
      },

      // Agente
      agente: {
        rfc: agenteRfc,
        nombre: agenteNombre,
      },

      // Primas
      primas: {
        prima,
        bonificacion,
        derechos,
        gastosExpedicion,
        gastosInvestigacion,
        subTotal,
        primaTotal,
        iva,
      },

      // Calculated fields
      tarifa,
    };

    // Critical fields — the XML is unusable without these.
    const criticalMissing = [];
    if (!numeroFianza) criticalMissing.push('Numero de Fianza');
    if (!montoAfianzado || montoAfianzado <= 0) criticalMissing.push('Monto Afianzado valido');
    if (!emisorNombre) criticalMissing.push('Nombre del Emisor');

    if (criticalMissing.length > 0) {
      return {
        success: false,
        data: null,
        error: `El XML no contiene los campos requeridos: ${criticalMissing.join(', ')}`,
        missingFields: criticalMissing,
      };
    }

    // Vigencia dates are FILLABLE: some fianza XMLs omit them. Accept the XML and
    // report which dates are missing (by field key) so the UI can prompt the user
    // to register them instead of rejecting the whole document.
    const missingDates = [];
    if (!inicioVigenciaFianza) missingDates.push('inicioVigenciaFianza');
    if (!finVigenciaFianza) missingDates.push('finVigenciaFianza');

    return {
      success: true,
      data,
      error: null,
      missingFields: missingDates,
      needsDates: missingDates.length > 0,
    };

  } catch (err) {
    console.error('Error al analizar XML:', err);
    return {
      success: false,
      data: null,
      error: err.message || 'Error al analizar el archivo XML. Verifica que sea un archivo valido.',
      missingFields: [],
    };
  }
}

/**
 * Parse XML Factura file (CFDI)
 * @param {File} file - The XML file to parse
 * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
 */
export async function parseXmlFactura(file) {
  try {
    const text = await file.text();
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(text, 'text/xml');

    // Check for parsing errors
    const parseError = xmlDoc.querySelector('parsererror');
    if (parseError) {
      return {
        success: false,
        data: null,
        error: 'Error al analizar el XML de la factura',
      };
    }

    // Get the root element (Comprobante in CFDI)
    const comprobante = xmlDoc.querySelector('Comprobante');
    if (!comprobante) {
      return {
        success: false,
        data: null,
        error: 'XML de factura invalido: no se encontro el elemento Comprobante',
      };
    }

    // Extract basic data from Comprobante
    const total = parseFloat(comprobante.getAttribute('Total'));
    const subTotal = parseFloat(comprobante.getAttribute('SubTotal'));
    const folio = comprobante.getAttribute('Folio');
    const serie = comprobante.getAttribute('Serie');
    const fecha = comprobante.getAttribute('Fecha');

    if (isNaN(total)) {
      return {
        success: false,
        data: null,
        error: 'No se pudo leer el monto total de la factura',
      };
    }

    // Extract Emisor data
    const emisor = xmlDoc.querySelector('Emisor');
    const emisorNombre = emisor?.getAttribute('Nombre');
    const emisorRfc = emisor?.getAttribute('Rfc');

    // Extract Receptor data
    const receptor = xmlDoc.querySelector('Receptor');
    const receptorNombre = receptor?.getAttribute('Nombre');
    const receptorRfc = receptor?.getAttribute('Rfc');

    // Extract Conceptos (line items)
    const conceptos = [];
    const conceptoElements = xmlDoc.querySelectorAll('Concepto');
    let noIdentificacion = null;

    conceptoElements.forEach(concepto => {
      const descripcion = concepto.getAttribute('Descripcion');
      const importe = parseFloat(concepto.getAttribute('Importe'));
      const noId = concepto.getAttribute('NoIdentificacion');

      // Store NoIdentificacion (should be the fianza number)
      if (noId && !noIdentificacion) {
        noIdentificacion = noId;
      }

      conceptos.push({
        descripcion,
        importe,
        noIdentificacion: noId,
      });
    });

    // Classify conceptos into prima vs gastos
    let primaAmount = 0;
    let gastosAmount = 0;
    conceptos.forEach(c => {
      if (c.descripcion && c.descripcion.toLowerCase().includes('prima')) {
        primaAmount += c.importe || 0;
      } else {
        gastosAmount += c.importe || 0;
      }
    });

    // Extract IVA from Impuestos - try multiple selector approaches
    let impuestos = xmlDoc.querySelector('Impuestos[TotalImpuestosTrasladados]');
    if (!impuestos) {
      impuestos = xmlDoc.getElementsByTagNameNS('http://www.sat.gob.mx/cfd/4', 'Impuestos')[0];
    }
    if (!impuestos) {
      impuestos = xmlDoc.querySelector('cfdi\\:Impuestos');
    }

    const totalImpuestosStr = impuestos ? impuestos.getAttribute('TotalImpuestosTrasladados') : null;
    const totalImpuestos = totalImpuestosStr ? parseFloat(totalImpuestosStr) : 0;

    // Extract tax rate from Traslado - try multiple approaches
    let traslado = xmlDoc.querySelector('Traslado[TasaOCuota]');
    if (!traslado) {
      traslado = xmlDoc.getElementsByTagNameNS('http://www.sat.gob.mx/cfd/4', 'Traslado')[0];
    }
    if (!traslado) {
      traslado = xmlDoc.querySelector('cfdi\\:Traslado');
    }

    const tasaStr = traslado ? traslado.getAttribute('TasaOCuota') : null;
    const tasaIVA = tasaStr ? parseFloat(tasaStr) * 100 : 16; // Default to 16% if not found

    // Extract UUID (Folio Fiscal) from TimbreFiscalDigital - try multiple approaches
    let timbreFiscal = xmlDoc.querySelector('TimbreFiscalDigital[UUID]');
    if (!timbreFiscal) {
      timbreFiscal = xmlDoc.getElementsByTagNameNS('http://www.sat.gob.mx/TimbreFiscalDigital', 'TimbreFiscalDigital')[0];
    }
    if (!timbreFiscal) {
      timbreFiscal = xmlDoc.querySelector('tfd\\:TimbreFiscalDigital');
    }

    const folioFiscal = timbreFiscal ? timbreFiscal.getAttribute('UUID') : null;
    const fechaTimbrado = timbreFiscal ? timbreFiscal.getAttribute('FechaTimbrado') : null;

    const data = {
      // Basic info
      folio,
      serie,
      fecha,
      total,
      subTotal,
      totalImpuestos,
      tasaIVA,

      // Timbre Fiscal Digital
      folioFiscal,
      fechaTimbrado,

      // Emisor
      emisor: {
        nombre: emisorNombre,
        rfc: emisorRfc,
      },

      // Receptor
      receptor: {
        nombre: receptorNombre,
        rfc: receptorRfc,
      },

      // Conceptos breakdown
      conceptos,

      // NoIdentificacion (should match fianza number)
      noIdentificacion,

      // Prima extracted from conceptos
      primas: {
        prima: primaAmount || null,
        gastos: gastosAmount || null,
        subTotal,
        iva: totalImpuestos,
        primaTotal: total,
      },
    };

    return {
      success: true,
      data,
      error: null,
    };

  } catch (err) {
    console.error('Error al analizar XML de factura:', err);
    return {
      success: false,
      data: null,
      error: err.message || 'Error al analizar el XML de la factura.',
    };
  }
}

/**
 * Comprehensive validation between fianza and factura data
 * Only runs when BOTH documents are present
 * @param {object} fianzaData - Parsed fianza data
 * @param {object} facturaData - Parsed factura data
 * @returns {Array<{type: 'critical'|'warning', field: string, message: string}>}
 */
export function validateFianzaFactura(fianzaData, facturaData) {
  // Skip validation if either document is missing
  if (!fianzaData || !facturaData) {
    return [];
  }

  const warnings = [];

  // 1. Check amount match (Prima Total vs Total Factura)
  if (fianzaData?.primas?.primaTotal && facturaData?.total) {
    const difference = Math.abs(fianzaData.primas.primaTotal - facturaData.total);

    if (difference > 0.01) {
      warnings.push({
        type: difference > 0.05 ? 'critical' : 'warning',
        field: 'Monto Total',
        message: `El monto de la factura (${new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(facturaData.total)}) no coincide con la prima total de la fianza (${new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(fianzaData.primas.primaTotal)}). Diferencia: ${new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(difference)}`,
      });
    }
  }

  // 2. Check ID Fianza vs No. Identificacion
  if (fianzaData?.numeroFianza && facturaData?.noIdentificacion) {
    if (fianzaData.numeroFianza !== facturaData.noIdentificacion) {
      warnings.push({
        type: 'critical',
        field: 'Numero de Fianza',
        message: `El numero de identificacion en la factura (${facturaData.noIdentificacion}) NO coincide con el numero de fianza (${fianzaData.numeroFianza})`,
      });
    }
  }

  // 3. Check RFC - Fiado (Fianza) vs Receptor (Factura)
  if (fianzaData?.fiado?.rfc && facturaData?.receptor?.rfc) {
    if (fianzaData.fiado.rfc !== facturaData.receptor.rfc) {
      warnings.push({
        type: 'critical',
        field: 'RFC del Fiado/Receptor',
        message: `El RFC del receptor en la factura (${facturaData.receptor.rfc}) NO coincide con el RFC del fiado en la fianza (${fianzaData.fiado.rfc})`,
      });
    }
  }

  // 4. Check RFC - Emisor (should match in both documents)
  if (fianzaData?.emisor?.rfc && facturaData?.emisor?.rfc) {
    if (fianzaData.emisor.rfc !== facturaData.emisor.rfc) {
      warnings.push({
        type: 'critical',
        field: 'RFC del Emisor',
        message: `El RFC del emisor en la factura (${facturaData.emisor.rfc}) NO coincide con el RFC del emisor en la fianza (${fianzaData.emisor.rfc})`,
      });
    }
  }

  return warnings;
}
